import React from 'react';
import { Cpu, FileCode, Layers } from 'lucide-react';

export type NarrativeProvenance = 'llm' | 'deterministic' | 'mixed';

interface Props {
  provenance: NarrativeProvenance;
  className?: string;
}

/**
 * Provenance chip attached to each text block (Dossier View panel review 2026-08-22 P2).
 * The outer executive summary said "Narrative written by an LLM"; the inner analytical block
 * said "Composed deterministically". Both could be true for different blocks — but a
 * panel-level either/or byline mis-labels one of them. Per-block chip removes the ambiguity.
 *
 * - deterministic: text composed by a template from math-core numbers only (never an LLM).
 * - llm: text produced by an LLM formatter (numbers still math-core-verified via readback).
 * - mixed: a block that stitches template scaffolding and LLM prose (rare; still labelled honestly).
 */
const NarrativeProvenanceChip: React.FC<Props> = ({ provenance, className }) => {
  const cfg = provenance === 'llm'
    ? { Icon: Cpu, label: 'LLM-formatted', title: 'Text produced by an LLM formatter. Numbers are math-core-derived and verified via narrative_readback.', tone: 'bg-indigo-900/30 border-indigo-800/60 text-indigo-300' }
    : provenance === 'mixed'
    ? { Icon: Layers, label: 'Template + LLM', title: 'Block stitches template scaffolding and LLM prose.', tone: 'bg-purple-900/30 border-purple-800/60 text-purple-300' }
    : { Icon: FileCode, label: 'Template-deterministic', title: 'Text composed by a template from math-core numbers only — no LLM.', tone: 'bg-transparent border-[color:rgb(var(--c-ink-rgb)/0.14)] text-[color:var(--c-text-body)]' };
  const { Icon, label, title, tone } = cfg;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[10px] font-mono ${tone} ${className ?? ''}`}
      title={title}
    >
      <Icon size={11} />
      {label}
    </span>
  );
};

export default NarrativeProvenanceChip;
